/**
 * Переключатель режима публикаций в шапке.
 *
 * ЧТО ОН ПЕРЕКЛЮЧАЕТ (с 10.09.2026). Канал: `live` — одобренные посты уходят в боевой канал
 * бренда, `test` — в его тестовый канал (`@smm/contracts` → api/operations.ts). Режим
 * закрепляется подписью на одобрении, поэтому пост, одобренный в тестовом режиме, в боевой
 * канал не уйдёт никогда, а боевой пост в тестовом режиме ждёт возврата в боевой. До 10.09
 * «тестовый» значил «в Telegram не уходит ничего», и подтверждение обязано говорить новую
 * правду словами: человек, помнящий прежний смысл, должен узнать его до нажатия.
 *
 * ПОЧЕМУ ПОДТВЕРЖДЕНИЕ ОБЯЗАТЕЛЬНО. Промах мышью здесь либо закрывает боевой канал на сутки,
 * либо открывает его ровно тогда, когда его закрыли ради проверки. Второй исход необратим:
 * ушедший в канал пост назад не забрать. Поэтому предупреждающий цвет у перехода в боевой.
 *
 * ПОЧЕМУ СОСТОЯНИЕ ЧИТАЕТСЯ БЕЗ НАВЕДЕНИЯ. Цвет дорожки и слово рядом с ней отвечают на
 * «куда сейчас уходят посты?» — вопрос, который нужен на каждом экране и ДО клика. Наведение
 * добавляет то, чего цветом не скажешь: кто переключил, когда, зачем и сколько постов ждёт.
 *
 * РЕЖИМ — ЭТОГО БРЕНДА (docs/multi-brand.md §3, 14.10.2026). У каждого бренда свой режим и свой
 * канал; переключение одного не трогает другой. Поэтому подсказка и подтверждение называют
 * бренд словами, а запрос переключения несёт его в теле: человек, переводящий GeoBoost в
 * боевой, не должен думать, что открыл боевой канал всем брендам, — и наоборот.
 *
 * СМЕНА РЕЖИМА ВЫКЛЮЧАЕТ АВТООДОБРЕНИЕ БРЕНДА (docs/ai-lab.md §3.5). Включивший его согласился
 * на канал прежнего режима; бэкенд выключает автоодобрение той же транзакцией, что пишет режим.
 * Если оно включено (`autoApprovalOn`), подтверждение говорит это до нажатия.
 */

import type { PublicationDispatchState } from '@smm/contracts/schemas';
import { type JSX, useId, useState } from 'react';
import { ApiRequestError, post } from '../api/client.ts';
import type { PublicationDispatch } from '../api/types.ts';
import { plural, shortMsk } from './format.ts';
import { Modal } from './Modal.tsx';
import styles from './ModeSwitch.module.css';

export interface ModeSwitchProps {
  /** Чей режим: id бренда из адреса — уходит в тело переключения. */
  readonly brand: string;
  /** Название бренда для текста подсказки и подтверждения. */
  readonly brandTitle: string;
  /** `undefined`, пока состояние не загружено или запрос провалился. */
  readonly dispatch: PublicationDispatch | undefined;
  readonly failed: boolean;
  /** Переключает только администратор (403 на бэкенде) — у остальных это индикатор. */
  readonly canManage: boolean;
  /**
   * Автоодобрение бренда сейчас включено. Смена режима его выключит (шапка файла), и
   * подтверждение обязано это назвать. Нет органа автоодобрения у бренда — `false`.
   */
  readonly autoApprovalOn?: boolean;
  readonly onChanged: () => void;
}

const STATE_WORD: Readonly<Record<PublicationDispatchState, string>> = {
  live: 'Боевой',
  test: 'Тест',
};

export function ModeSwitch({
  brand,
  brandTitle,
  dispatch,
  failed,
  canManage,
  autoApprovalOn = false,
  onChanged,
}: ModeSwitchProps): JSX.Element | null {
  const tipId = useId();
  const [confirming, setConfirming] = useState(false);

  // Пока состояние неизвестно, шапка молчит: мигание боевого режима на полсекунды — это
  // утверждение «посты уходят подписчикам», сделанное до того, как сервер это подтвердил.
  if (dispatch === undefined) {
    if (!failed) return null;
    return (
      <div className={styles['wrap']}>
        {/* Не кнопка: нажимать нечего, пока неизвестно, что переключаешь. Имя даёт `title` —
            `aria-label` на элементе без роли ARIA не читает. */}
        <span
          className={`${styles['sw']} ${styles['unknown']}`}
          title="Режим публикаций неизвестен"
        >
          <span className={styles['track']}>
            <span className={styles['knob']} />
          </span>
          <span className={styles['label']}>—</span>
        </span>
        <span className={styles['tip']} role="tooltip">
          <b>Режим публикаций неизвестен</b>
          <span>{`Состояние режима ${brandTitle} не загрузилось. Обновите страницу.`}</span>
        </span>
      </div>
    );
  }

  const live = dispatch.state === 'live';

  return (
    <div className={styles['wrap']}>
      <button
        type="button"
        role="switch"
        aria-checked={live}
        aria-describedby={tipId}
        aria-label={`Режим публикаций ${brandTitle}: ${live ? 'боевой' : 'тестовый'}`}
        className={`${styles['sw']} ${live ? styles['live'] : styles['test']}`}
        disabled={!canManage}
        onClick={() => {
          setConfirming(true);
        }}
      >
        <span className={styles['track']}>
          <span className={styles['knob']} />
        </span>
        <span className={styles['label']}>{STATE_WORD[dispatch.state]}</span>
      </button>

      <span className={styles['tip']} role="tooltip" id={tipId}>
        <b>{`${live ? 'Боевой режим' : 'Тестовый режим'} · ${brandTitle}`}</b>
        <span>
          {live
            ? 'Одобренные посты уходят в боевой канал по расписанию.'
            : 'Одобренные посты уходят в тестовый канал. Боевой канал закрыт: посты, одобренные в боевом режиме, ждут возврата в него.'}
        </span>
        <span className={styles['tipNote']}>
          Режим у каждого бренда свой — у других брендов он не меняется.
        </span>
        {dispatch.waiting_jobs > 0 ? (
          <span className={styles['tipNote']}>
            {`Ждут ${live ? 'тестового' : 'боевого'} режима: ${String(dispatch.waiting_jobs)} ${plural(dispatch.waiting_jobs, 'пост', 'поста', 'постов')}`}
          </span>
        ) : null}
        {dispatch.changed_at === null ? (
          // Умолчание — тестовый режим (с 15.09.2026); приписывать его человеку нельзя.
          <span className={styles['tipNote']}>Не переключался.</span>
        ) : dispatch.changed_by_login === null ? (
          // Момент есть, а человека нет: режим выставлен не из панели — выкладкой. «Не
          // переключался» здесь было бы ложью о том, куда прямо сейчас уходят посты.
          <span className={styles['tipNote']}>
            {`вне панели · ${shortMsk(dispatch.changed_at)}`}
            {dispatch.note === null ? '' : ` · ${dispatch.note}`}
          </span>
        ) : (
          <span className={styles['tipNote']}>
            {`${dispatch.changed_by_login} · ${shortMsk(dispatch.changed_at)}`}
            {dispatch.note === null ? '' : ` · ${dispatch.note}`}
          </span>
        )}
        {canManage ? null : <span className={styles['tipNote']}>Переключает администратор.</span>}
      </span>

      {confirming ? (
        <ModeConfirm
          brand={brand}
          brandTitle={brandTitle}
          target={live ? 'test' : 'live'}
          waitingJobs={dispatch.waiting_jobs}
          inFlightJobs={dispatch.in_flight_jobs}
          autoApprovalOn={autoApprovalOn}
          onClose={() => {
            setConfirming(false);
          }}
          onDone={() => {
            setConfirming(false);
            onChanged();
          }}
        />
      ) : null}
    </div>
  );
}

/**
 * Подтверждение. Последствие названо числом, а не общими словами: «уйдут сразу N постов» —
 * единственное, чего человек может не ожидать, переключая режим: это работы, которые держал
 * прежний режим (`waiting_jobs`), и они уйдут в канал нового режима на ближайшем тике.
 */
function ModeConfirm({
  brand,
  brandTitle,
  target,
  waitingJobs,
  inFlightJobs,
  autoApprovalOn,
  onClose,
  onDone,
}: {
  brand: string;
  brandTitle: string;
  target: PublicationDispatchState;
  waitingJobs: number;
  /** Работы нынешнего канала уже у публикатора: переключение их не обещает остановить. */
  inFlightJobs: number;
  autoApprovalOn: boolean;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const noteId = useId();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toTest = target === 'test';

  const submit = (): void => {
    setBusy(true);
    setError(null);
    post<PublicationDispatch>('/api/v1/publication-dispatch', {
      brand,
      state: target,
      note: note.trim() === '' ? null : note.trim(),
    })
      .then(() => {
        onDone();
      })
      .catch((e: unknown) => {
        setBusy(false);
        setError(e instanceof ApiRequestError || e instanceof Error ? e.message : String(e));
      });
  };

  const now =
    waitingJobs === 0
      ? ''
      : toTest
        ? ` ${String(waitingJobs)} ${plural(waitingJobs, 'тестовый пост уйдёт', 'тестовых поста уйдут', 'тестовых постов уйдут')} в тестовый канал сразу.`
        : ` ${String(waitingJobs)} ${plural(waitingJobs, 'пост уйдёт', 'поста уйдут', 'постов уйдут')} в боевой канал сразу.`;

  // Выданное публикатору переключение не держит: пост, чьё намерение отправки уже
  // записано, уйдёт; тот, что ещё проверяется, получит отказ и вернётся человеку. Сказать
  // «остановится» нельзя, промолчать — тем более: ради этого человек и переключает.
  const inFlight =
    inFlightJobs === 0
      ? ''
      : ` Сейчас у публикатора ${String(inFlightJobs)} ${plural(inFlightJobs, 'пост', 'поста', 'постов')} ${toTest ? 'боевого' : 'тестового'} канала: если отправка уже началась, переключение её не остановит.`;

  return (
    <Modal
      title={`Перевести ${brandTitle} в ${toTest ? 'тестовый' : 'боевой'} режим?`}
      width={460}
      onClose={onClose}
    >
      <div className={toTest ? 'val val-info' : 'val val-warn'}>
        <span>
          {toTest
            ? 'Одобренные посты пойдут в тестовый канал, боевой канал закроется. Посты, уже одобренные в боевом режиме, не пропадут — они дождутся возврата в боевой режим.'
            : 'Одобренные посты пойдут в боевой канал — их увидят подписчики. Посты, одобренные в тестовом режиме, в боевой канал не попадут: они подписаны на тестовый.'}
          {now}
          {inFlight}
          {toTest
            ? ''
            : ' Если боевой канал в конфиге бренда не задан, одобрения в этом режиме будут отказывать, пока id не впишут.'}
          {' Режим других брендов не меняется.'}
        </span>
      </div>
      {autoApprovalOn ? (
        <div className="val val-warn">
          <span>
            Автоодобрение бренда будет выключено: автоодобренные посты в слотах вернутся на
            согласование. Включить заново — с подтверждением канала нового режима.
          </span>
        </div>
      ) : null}

      <div>
        <label className="lbl" htmlFor={noteId}>
          Причина
        </label>
        <input
          id={noteId}
          className="inp"
          value={note}
          maxLength={500}
          placeholder={toTest ? 'проверка, деплой…' : 'необязательно'}
          onChange={(e) => {
            setNote(e.target.value);
          }}
        />
      </div>

      {error === null ? null : (
        <div className="val val-err" role="alert">
          <span>{error}</span>
        </div>
      )}

      <div className={styles['actions']}>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          Отмена
        </button>
        <button
          type="button"
          className={busy ? 'btn btn-sm btn-dis' : 'btn btn-pri btn-sm'}
          disabled={busy}
          onClick={submit}
        >
          Перевести
        </button>
      </div>
    </Modal>
  );
}
